/* eslint-disable max-lines */
import React, { useCallback, useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import AlertIconWithCount from 'pages/shared/Alert/IconWithCount'
import Card from 'components/Card'
import Pagination from 'components/Pagination'
import Pin from 'pages/shared/Pin'

import Apptentive from 'helpers/apptentive'
import { customerDisplayName } from 'helpers/formatters'
import GA4 from 'helpers/ga4'

import usePagedCustomers from 'hooks/usePagedCustomers'
import { useStoreContext } from 'hooks/StoreContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import { color } from 'styles/colors'
import { NoData, screenSize } from 'styles/shared'

import { Customer } from 'types/Customer'

const ASC_SORT = 'asc'

interface PinnedCustomersPanelProps {
  variant?: 'single-column' | 'two-column'
  pageSize?: string
  showTitle?: boolean
  showBottomPagination?: boolean
}

const PinnedCustomersPanel = ({
  variant = 'single-column',
  pageSize = '10',
  showTitle = true,
  showBottomPagination = false
}: PinnedCustomersPanelProps) => {
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now())
  const [page, setPage] = useState(1)

  usePagedCustomers({
    query: '',
    filters: 'pinned',
    page,
    sortAttribute: 'customer',
    sortDirection: ASC_SORT,
    size: pageSize,
    refreshTimestamp
  })

  const { customersMetadata, arePagedCustomersLoaded, customers } = useStoreContext()
  const { enabledFeatures } = useFeaturesContext()

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const refreshPageCallback = useCallback(() => {
    setRefreshTimestamp(Date.now())
  }, [])

  const isTwoColumn = variant === 'two-column'
  const paginationElement = customersMetadata?.pageCount > 1
    ? (
      <S.Pagination
        size="medium"
        pageCount={customersMetadata?.pageCount}
        onPageChange={handlePageChange}
        page={customersMetadata?.pageNumber}
      />
    )
    : null

  const hasNoPinnedCustomers = arePagedCustomersLoaded && enabledFeatures?.includes('hehm') && (!customers || customers.length === 0)
  const customersList = (
    <>
      {hasNoPinnedCustomers && (
        <S.NoData>No pinned customers</S.NoData>
      )}
      <S.CustomersContainer $isTwoColumn={isTwoColumn} data-testid="pinned-customers-card">
        {customers && customers.map((customer: Customer) => {
          const displayName = customerDisplayName(
            customer.firstName,
            customer.lastName,
            customer.companyName
          )

          const sendEvent = () => {
            const gAEvent = {
              category: 'Dashboard Page',
              action: 'Click Pinned Customer Name',
              label: displayName
            }

            GA4.sendEvent(gAEvent)
            Apptentive.sendEvent('view_customer_from_pinned')
          }

          const hasAlerts = (
            (customer.criticalAlarmCount ?? 0) > 0 ||
            (customer.sdaCriticalAlarmCount ?? 0) > 0 ||
            (customer.majorAlarmCount ?? 0) > 0 ||
            (customer.sdaMajorAlarmCount ?? 0) > 0 ||
            (customer.minorAlarmCount ?? 0) > 0 ||
            (customer.sdaMinorAlarmCount ?? 0) > 0
          )

          return (
            <S.PinnedCustomer key={`customer-id:${customer.id}`} $isTwoColumn={isTwoColumn}>
              <S.LeftColumn>
                <S.Pin
                isPinned={customer.pinned}
                customerName={displayName}
                customerId={customer.id}
                pinnedAt={customer.pinnedAt}
                displayUnpinned
                refreshPageCallback={refreshPageCallback}
                />

                <NavLink to={`/customers/${customer.id}`} onClick={sendEvent}>
                  {displayName}
                </NavLink>
              </S.LeftColumn>

              {isTwoColumn ? (
                hasAlerts && (
                <S.AlertsContainer>
                  {(customer.criticalAlarmCount ?? 0) > 0 && (
                  <S.AlertWrapper>
                    <AlertIconWithCount severity="critical" count={customer.criticalAlarmCount} />
                  </S.AlertWrapper>
                  )}
                  {(customer.sdaCriticalAlarmCount ?? 0) > 0 && (
                  <S.AlertWrapper>
                    <AlertIconWithCount severity="critical-sda" count={customer.sdaCriticalAlarmCount} />
                  </S.AlertWrapper>
                  )}
                  {(customer.majorAlarmCount ?? 0) > 0 && (
                  <S.AlertWrapper>
                    <AlertIconWithCount severity="major" count={customer.majorAlarmCount} />
                  </S.AlertWrapper>
                  )}
                  {(customer.sdaMajorAlarmCount ?? 0) > 0 && (
                  <S.AlertWrapper>
                    <AlertIconWithCount severity="major-sda" count={customer.sdaMajorAlarmCount} />
                  </S.AlertWrapper>
                  )}
                  {(customer.minorAlarmCount ?? 0) > 0 && (
                  <S.AlertWrapper>
                    <AlertIconWithCount severity="minor" count={customer.minorAlarmCount} />
                  </S.AlertWrapper>
                  )}
                  {(customer.sdaMinorAlarmCount ?? 0) > 0 && (
                  <S.AlertWrapper>
                    <AlertIconWithCount severity="minor-sda" count={customer.sdaMinorAlarmCount} />
                  </S.AlertWrapper>
                  )}
                </S.AlertsContainer>
                )
              ) : (
                <>
                  <AlertIconWithCount severity="critical" count={customer.criticalAlarmCount} />
                  <AlertIconWithCount severity="major" count={customer.majorAlarmCount} />
                  <AlertIconWithCount severity="major-sda" count={customer.sdaMajorAlarmCount} />
                  <AlertIconWithCount severity="minor" count={customer.minorAlarmCount} />
                  <AlertIconWithCount severity="minor-sda" count={customer.sdaMinorAlarmCount} />
                </>
              )}
            </S.PinnedCustomer>
          )
        })}
      </S.CustomersContainer>
    </>
  )

  const cardContent = showBottomPagination ? (
    <S.BottomPaginationWrapper $hasNoPinnedCustomers={hasNoPinnedCustomers}>
      {customersList}
      {paginationElement}
    </S.BottomPaginationWrapper>
  ) : (
    <>
      {customersList}
    </>
  )

  return (
    <S.Card
      $isTwoColumn={isTwoColumn}
      $showBottomPagination={showBottomPagination}
      $hasNoPinnedCustomers={hasNoPinnedCustomers}
      title={showTitle ? PinnedCustomersPanel.cardTitle : undefined}
      titleAreaRight={paginationElement}
      titleAreaStyle={`
        @media ${screenSize.small} {
          justify-content: unset;
          flex-direction: column;
        }
      `}
      isLoaded={arePagedCustomersLoaded || !!customers}
    >
      {cardContent}
    </S.Card>
  )
}

PinnedCustomersPanel.cardTitle = 'Pinned Customers'
PinnedCustomersPanel.cardKey = 'pinned_customers'

export default PinnedCustomersPanel

const S = {
  NoData: styled(NoData)`
    padding: 40px 0;
    font-weight: bold;
  `,
  Card: styled(Card)<{ $isTwoColumn: boolean; $showBottomPagination?: boolean; $hasNoPinnedCustomers?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${({ $isTwoColumn }) => $isTwoColumn ? '16px' : '0'};
  `,
  BottomPaginationWrapper: styled.div<{ $hasNoPinnedCustomers?: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: ${({ $hasNoPinnedCustomers }) => $hasNoPinnedCustomers ? 'unset' : 'fit-content'};
  `,
  Pagination: styled(Pagination)`
    align-self: center;
    margin-top: auto;
  `,
  CustomersContainer: styled.div<{ $isTwoColumn: boolean }>`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    gap: 8px;
    padding-bottom: 16px;

    > * {
      flex: 1 1 calc(50% - 4px);
    }

    @media ${screenSize.small} {
      > * {
        flex: 1 1 100%;
      }
    }
  `,
  PinnedCustomer: styled.div<{ $isTwoColumn: boolean }>`
    display: flex;
    padding: 8px 0;
    margin-top: 10px;
    border: 1px solid ${color('border')};
    border-radius: 6px;
    background-color: ${({ $isTwoColumn }) => $isTwoColumn ? color('buttonBackground') : 'transparent'};
    width: 100%;
    align-items: center;
  `,
  LeftColumn: styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;

    a {
      word-break: break-word;
      overflow-wrap: break-word;
    }
  `,
  Pin: styled(Pin)`
    position: relative;
    top: 3px;
    margin: 0 8px 0 8px;
    flex-shrink: 0;
  `,
  AlertsContainer: styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, 55px);
    justify-content: flex-end;
    max-width: 165px;
  `,
  AlertWrapper: styled.div<{ isTwoColumn?: boolean }>`
    width: 55px;
    display: flex;
    justify-content: center;
  `
}
